// src/pages/sets-page/components/create-set-form/CreateSetForm.js
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useCreateSetMutation } from '../../../../entities/sets/sets.api';
import { selectUser } from '../../../../store/user/user.selector';
import { setSystemNotification } from '../../../../store/system/system.action';
import {
  CreateSetFormContainer,
  FormContainer,
  TagsContainer,
  TagList,
  TagItem,
  TagRemoveButton,
  ButtonGroup,
  ErrorMessage
} from './CreateSetForm.styles.js';

// components
import Title from '../../../../components/widgets/components/title/Title.js';
import InputBar from '../../../../components/input-bar/InputBar.js';
import InputTextfield from '../../../../components/input-textfield/InputTextfield.js';
import InputTags from '../../../../components/input-tags/InputTags.js';
import PrimaryBtn from '../../../../components/btns/primary-btn/PrimaryBtn.js';
import SecondaryBtn from '../../../../components/btns/secondary-btn/SecondaryBtn.js';

export default function CreateSetForm({ onCancel, onSuccess }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [createSet, { isLoading }] = useCreateSetMutation();

  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const newTag = tagInput.trim();

      if (newTag && !tags.includes(newTag)) {
        setTags(prev => [...prev, newTag]);
        setTagInput('');
      }
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(prev => prev.filter(tag => tag !== tagToRemove));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Set name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Set name must be 100 characters or less';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const setData = {
        title: formData.name.trim(),
        description: formData.description?.trim() || '',
        tags: tags
      };

      const result = await createSet({
        uid: user.uid,
        setData
      }).unwrap();

      dispatch(setSystemNotification(`Set "${setData.title}" created successfully!`));
      onSuccess?.(result);

      // Navigate to the created set
      navigate(`/sets/${result.setId}`);

      // Reset form
      setFormData({
        name: '',
        description: ''
      });
      setTags([]);
      setTagInput('');
      setErrors({});

    } catch (error) {
      console.error('Error creating set:', error);
      dispatch(setSystemNotification(`Error creating set: ${error.message}`));
    }
  };

  return (
    <CreateSetFormContainer className='widget-bg'>
      <Title text="CREATE NEW SET" />

      <FormContainer onSubmit={handleSubmit}>
        <InputBar
          type="text"
          id="set-name"
          label="Set Name *"
          value={formData.name}
          updateValue={(value) => handleInputChange({ target: { name: 'name', value } })}
        />
        {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}

        <InputTextfield
          id="description"
          label="Description"
          value={formData.description}
          updateValue={(value) => handleInputChange({ target: { name: 'description', value } })}
          maxLength={500}
          rows={3}
        />
        {errors.description && <ErrorMessage>{errors.description}</ErrorMessage>}

        <TagsContainer>
          <InputTags
            id="tag-input"
            label="Tags"
            value={tagInput}
            updateValue={setTagInput}
            onKeyDown={handleAddTag}
          />
          {tags.length > 0 && (
            <TagList>
              {tags.map((tag, index) => (
                <TagItem key={index}>
                  {tag}
                  <TagRemoveButton
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                  >
                    ×
                  </TagRemoveButton>
                </TagItem>
              ))}
            </TagList>
          )}
        </TagsContainer>

        <ButtonGroup>
          <SecondaryBtn
            title="CANCEL"
            onClick={onCancel}
            type="button"
          />
          <PrimaryBtn
            title={isLoading ? 'CREATING...' : 'SAVE'}
            onClick={handleSubmit}
            disabled={isLoading || !formData.name.trim()}
            type="button"
          />
        </ButtonGroup>
      </FormContainer>
    </CreateSetFormContainer>
  );
}
